import React from 'react';
import { Modal } from '@components/molecules/Modal';
import Search from '@components/ui/Search';
import { Avatar } from '@components/atoms/Avatar';
import { useAllEmployees } from '@hooks/api';
import { useAssignEmployeesToCrew } from '@hooks/api/crew/useAssignEmployeesToCrew';
import { useCrewUnassignEmployee } from '@hooks/api/crew/useCrewUnassignEmployee';
import { Loading } from '@components/atoms/Loading';
import { Crew } from '@models/crew.model';
import { Employee } from '@models/employee.model';
import { UserRoles } from '@models/user.model';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { getTranslatedRoleLabel } from '@constants/roleHierarchy';
import { useTranslation } from '@hooks/useTranslation';

type Props = {
  crew: Crew | null;
  crewId: string;
  isOpen: boolean;
  onClose: () => void;
};

/** An employee flattened into everything a transfer row needs to render. */
type Candidate = {
  employeeId: string;
  name: string;
  email?: string;
  avatar?: string;
  roleLabel: string;
  /** Crews this person belongs to *other* than the one being edited. */
  otherCrews: string[];
};

/** Only these two roles can be crew members (the API also rejects REVIEWER). */
const CREW_ELIGIBLE_ROLES: string[] = [UserRoles.EMPLOYEE, UserRoles.CREW_LEAD];

const displayName = (e: Employee): string =>
  `${e.user?.firstName ?? ''} ${e.user?.lastName ?? ''}`.trim() ||
  e.user?.email ||
  e.employeeId ||
  'Employee';

const TransferRow: React.FC<{
  candidate: Candidate;
  direction: 'add' | 'remove';
  disabled?: boolean;
  onClick: () => void;
}> = ({ candidate, direction, disabled, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={direction === 'add' ? 'Add to crew' : 'Remove from crew'}
    className="group w-full flex items-start gap-3 px-3 py-2 text-left rounded-md transition-colors hover:bg-indigo-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent">
    {direction === 'remove' && (
      <ArrowBackIcon
        className="mt-1.5 text-gray-300 group-hover:text-indigo-500 shrink-0"
        sx={{ fontSize: '18px' }}
      />
    )}

    <div className="shrink-0 mt-0.5">
      <Avatar size="sm" src={candidate.avatar} text={candidate.name} />
    </div>

    <div className="min-w-0 flex-1 flex flex-col gap-0.5">
      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-900 dark:text-gray-100 truncate">
          {candidate.name}
        </span>
        <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded px-1.5 py-0.5">
          {candidate.roleLabel}
        </span>
      </div>

      {candidate.email && (
        <p className="text-xs text-gray-500 dark:text-gray-400 truncate m-0">
          {candidate.email}
        </p>
      )}

      {/* Crew names are user-supplied and often long, so let them wrap onto
          their own lines as chips rather than truncating a joined string. */}
      {candidate.otherCrews.length > 0 && (
        <div className="flex flex-wrap items-center gap-1 mt-0.5">
          <span className="text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500">
            Also in
          </span>
          {candidate.otherCrews.map((crewName) => (
            <span
              key={crewName}
              title={crewName}
              className="max-w-full truncate text-[10px] font-medium text-indigo-700 dark:text-indigo-200 bg-indigo-50 dark:bg-indigo-900/40 border border-indigo-100 dark:border-indigo-800 rounded px-1.5 py-0.5">
              {crewName}
            </span>
          ))}
        </div>
      )}
    </div>

    {direction === 'add' && (
      <ArrowForwardIcon
        className="mt-1.5 text-gray-300 group-hover:text-indigo-500 shrink-0"
        sx={{ fontSize: '18px' }}
      />
    )}
  </button>
);

const TransferColumn: React.FC<{
  title: string;
  count: number;
  direction: 'add' | 'remove';
  candidates: Candidate[];
  emptyText: string;
  isLoading?: boolean;
  disabled?: boolean;
  actionLabel: string;
  onAction: () => void;
  onRowClick: (employeeId: string) => void;
}> = ({
  title,
  count,
  direction,
  candidates,
  emptyText,
  isLoading,
  disabled,
  actionLabel,
  onAction,
  onRowClick,
}) => (
  <div className="flex flex-col border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden min-w-0">
    <div className="flex items-center justify-between gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
      <span className="text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-200 truncate">
        {title} ({count})
      </span>
      <button
        type="button"
        onClick={onAction}
        disabled={disabled || candidates.length === 0}
        className="shrink-0 text-xs font-medium text-indigo-600 dark:text-indigo-300 hover:underline disabled:opacity-40 disabled:no-underline disabled:cursor-not-allowed">
        {actionLabel}
      </button>
    </div>

    <div className="h-72 overflow-y-auto p-1">
      {isLoading ? (
        <div className="h-full flex items-center justify-center">
          <Loading size="lg" />
        </div>
      ) : candidates.length === 0 ? (
        <p className="h-full flex items-center justify-center text-center text-xs text-gray-500 dark:text-gray-400 px-4 m-0">
          {emptyText}
        </p>
      ) : (
        candidates.map((candidate) => (
          <TransferRow
            key={candidate.employeeId}
            candidate={candidate}
            direction={direction}
            disabled={disabled}
            onClick={() => onRowClick(candidate.employeeId)}
          />
        ))
      )}
    </div>
  </div>
);

export const ManageCrewMembersModal: React.FC<Props> = ({
  crew,
  crewId,
  isOpen,
  onClose,
}) => {
  // Full roster (unpaginated) — a paginated page would silently hide eligible
  // members (e.g. crew leads) once the org has more than one page of users.
  const { data: employeesData, isLoading } = useAllEmployees();
  const { mutateAsync: assign, isPending: isAssigning } =
    useAssignEmployeesToCrew();
  const { mutateAsync: unassign, isPending: isUnassigning } =
    useCrewUnassignEmployee();
  const tEnumLabels = useTranslation('enumLabels');
  const tCommon = useTranslation('common');

  const [search, setSearch] = React.useState('');
  const isSaving = isAssigning || isUnassigning;

  // Key on the membership *set*, not on array identity: a background refetch of
  // the crews query hands back new objects every time, and resetting off that
  // would wipe the user's in-progress edits.
  const memberIdsKey = (crew?.employees || [])
    .map((e) => e.employeeId)
    .filter((id): id is string => Boolean(id))
    .sort()
    .join(',');
  const memberIds = React.useMemo(
    () => (memberIdsKey ? memberIdsKey.split(',') : []),
    [memberIdsKey],
  );

  /** The crew's *desired* membership — what Save will persist. */
  const [selectedIds, setSelectedIds] = React.useState<string[]>(memberIds);
  React.useEffect(() => setSelectedIds(memberIds), [memberIds]);

  // Every eligible employee in the organization — membership in this or any
  // other crew is shown as information, never used to exclude anyone.
  const candidates: Candidate[] = React.useMemo(() => {
    const roster = Array.isArray(employeesData) ? employeesData : [];
    return roster
      .filter(
        (e) => e.employeeId && CREW_ELIGIBLE_ROLES.includes(e.user?.role ?? ''),
      )
      .map((e) => ({
        employeeId: e.employeeId as string,
        name: displayName(e),
        email: e.user?.email,
        avatar: e.user?.avatar,
        roleLabel: getTranslatedRoleLabel(e.user?.role, tEnumLabels, tCommon.unknown),
        otherCrews: (e.crews || [])
          .filter((c) => c.crewId !== crewId)
          .map((c) => c.name)
          .filter(Boolean),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [employeesData, crewId, tEnumLabels, tCommon.unknown]);

  const selectedSet = new Set(selectedIds);
  const term = search.trim().toLowerCase();
  const matchesSearch = (c: Candidate) =>
    !term ||
    c.name.toLowerCase().includes(term) ||
    (c.email ?? '').toLowerCase().includes(term);

  const members = candidates.filter((c) => selectedSet.has(c.employeeId));
  const available = candidates.filter((c) => !selectedSet.has(c.employeeId));
  const visibleMembers = members.filter(matchesSearch);
  const visibleAvailable = available.filter(matchesSearch);

  const initialSet = new Set(memberIds);
  const hasChanges =
    selectedIds.length !== memberIds.length ||
    selectedIds.some((id) => !initialSet.has(id));

  const addMember = (employeeId: string) =>
    setSelectedIds((prev) =>
      prev.includes(employeeId) ? prev : [...prev, employeeId],
    );
  const removeMember = (employeeId: string) =>
    setSelectedIds((prev) => prev.filter((id) => id !== employeeId));

  const handleClose = () => {
    if (isSaving) return;
    onClose();
  };

  const handleSave = async () => {
    if (!crewId || isSaving) return;

    try {
      if (selectedIds.length > 0) {
        // `assignEmployeesToCrew` replaces the whole member set, so the current
        // selection covers both additions and removals in one call.
        await assign({ crewId, employeeIds: selectedIds });
      } else {
        // The assign mutation rejects an empty list, so drain the crew one
        // member at a time through the unassign mutation instead.
        for (const employeeId of memberIds) {
          await unassign({ crewId, employeeId });
        }
      }
      onClose();
    } catch {
      // Both mutation hooks surface the failure via toast; keep the modal open
      // so the user's changes aren't lost.
    }
  };

  return (
    <Modal
      title="Manage Crew Members"
      description="Click a person to move them between lists. Employees can belong to more than one crew."
      isOpen={isOpen}
      onClose={handleClose}
      onAfterClose={() => {
        setSelectedIds(memberIds);
        setSearch('');
      }}
      isLoading={isSaving}
      confirmText="Save Changes"
      handleConfirm={handleSave}
      disableConfirm={isSaving || !hasChanges}
      closeOnOverlayClick={!isSaving}
      closeOnEscape={!isSaving}
      size="large">
      <div className="flex flex-col gap-4">
        <Search
          placeholder="Search by name or email..."
          value={search}
          disabled={isSaving}
          onChange={(event) => setSearch(event.target.value)}
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <TransferColumn
            title="Available"
            count={available.length}
            direction="add"
            candidates={visibleAvailable}
            isLoading={isLoading}
            disabled={isSaving}
            actionLabel="Add all"
            onAction={() =>
              setSelectedIds((prev) => [
                ...prev,
                ...visibleAvailable.map((c) => c.employeeId),
              ])
            }
            onRowClick={addMember}
            emptyText={
              term
                ? 'No matches.'
                : available.length === 0 && candidates.length > 0
                  ? 'Everyone eligible is already in this crew.'
                  : 'No employees or crew leads in this organization yet.'
            }
          />

          <TransferColumn
            title="In this crew"
            count={members.length}
            direction="remove"
            candidates={visibleMembers}
            disabled={isSaving}
            actionLabel="Remove all"
            onAction={() => {
              const visible = new Set(
                visibleMembers.map((c) => c.employeeId),
              );
              setSelectedIds((prev) => prev.filter((id) => !visible.has(id)));
            }}
            onRowClick={removeMember}
            emptyText={
              term ? 'No matches.' : 'No members yet — add people from the left.'
            }
          />
        </div>
      </div>
    </Modal>
  );
};
